import { useMemo, useState, useContext } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Loader2 } from '../components/icons';
import { Modal } from '../components/ui/Modal';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import { AuthContext } from '../context/authContext';

const BLOQUES = [
    { clave: 'caracteristicas_generales', label: 'Caract. Generales' },
    { clave: 'personeria', label: 'Personería' },
    { clave: 'predio', label: 'Predio' },
    { clave: 'infraestructura', label: 'Infraestructura' },
    { clave: 'disciplinas', label: 'Disciplinas' },
    { clave: 'diagnostico', label: 'Diagnóstico' },
];

const BadgePorcentaje = ({ cargados, total }) => {
    const pct = total > 0 ? Math.round((cargados / total) * 100) : 0;
    const color = pct === 100 ? 'bg-green-100 text-green-700'
        : pct >= 50 ? 'bg-amber-100 text-amber-700'
        : pct > 0 ? 'bg-orange-100 text-orange-700'
        : 'bg-gray-100 text-gray-400';
    return (
        <span title={`${cargados} / ${total} campos`} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${color}`}>
            {pct}%
        </span>
    );
};

const BadgeResponsable = ({ responsable }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        responsable ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
    }`}>
        {responsable ? 'SI' : 'NO'}
    </span>
);

export const EstadisticasPage = () => {
    const { role, userMunicipio } = useContext(AuthContext);
    const esMunicipio = role === 'editor_municipio';
    const partidoInicial = esMunicipio && userMunicipio ? [userMunicipio] : [];

    const [filtros, setFiltros] = useState({
        partido: partidoInicial,
        region: '',
        seccion: [],
        tipoEntidad: '',
        personeria: '',
        predio: '',
        deporteAdaptado: '',
        servicio: '',
    });
    const [busqueda, setBusqueda] = useState('');
    const [filaDetalle, setFilaDetalle] = useState(null);

    const handleFilterChange = (e) => {
        setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetFiltros = () => {
        setFiltros({
            partido: partidoInicial, region: '', seccion: [], tipoEntidad: '',
            personeria: '', predio: '', deporteAdaptado: '', servicio: '',
        });
    };

    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        Object.entries(filtros).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach(item => { if (item && item !== '') p.append(k, item); });
            } else if (v && v !== '') {
                p.set(k, v);
            }
        });
        const s = p.toString();
        return s ? `?${s}` : '';
    }, [filtros]);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['estadisticas-entidades', queryParams],
        queryFn: async () => {
            const rawToken = localStorage.getItem('token');
            const token = rawToken ? JSON.parse(rawToken) : null;
            const res = await fetch(`/api/entidades/estadisticas${queryParams}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error('Error al obtener estadísticas');
            return res.json();
        },
        placeholderData: keepPreviousData,
    });

    const filas = useMemo(() => {
        const rows = data || [];
        if (!busqueda.trim()) return rows;
        const q = busqueda.trim().toLowerCase();
        return rows.filter(r =>
            (r.nombre_completo || '').toLowerCase().includes(q) ||
            (r.id_red || '').toLowerCase().includes(q)
        );
    }, [data, busqueda]);

    const resumenCarga = useMemo(() => {
        const total = filas.length;
        if (total === 0) return null;
        return BLOQUES.map(b => {
            const cargadas = filas.filter(r => (r.bloque?.[b.clave]?.cargados || 0) > 0).length;
            return {
                ...b,
                cantidad: cargadas,
                totalEntidades: total,
                porcentaje: ((cargadas / total) * 100).toFixed(1).replace('.', ','),
            };
        });
    }, [filas]);

    return (
        <div className="w-full p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                        Estadísticas de Carga
                    </h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300">
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        En desarrollo
                    </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Qué bloques de datos se cargaron por club
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Este tablero se encuentra en etapa de desarrollo: la información puede cambiar.
                </p>
            </div>

            {/* Filtros */}
            <DashboardFilters
                filtros={filtros}
                onFilterChange={handleFilterChange}
                onReset={resetFiltros}
                loading={isLoading}
                esMunicipio={esMunicipio}
                userMunicipio={userMunicipio}
            />

            {/* Buscar club */}
            <div className="mb-6 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-4">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Buscar Entidad Deportiva
                </label>
                <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Nombre o ID Red..."
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none"
                />
            </div>

            {/* Resumen de carga */}
            {resumenCarga && (
                <div className="mb-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
                    <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-4 text-center">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Entidades</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                            {resumenCarga[0]?.totalEntidades.toLocaleString('es-AR')}
                        </p>
                        <p className="text-xs text-gray-400">Base de cálculo</p>
                    </div>
                    {resumenCarga.map(b => (
                        <div key={b.clave} className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-4 text-center">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{b.label}</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {b.cantidad.toLocaleString('es-AR')}
                            </p>
                            <p className="text-xs text-gray-400">{b.porcentaje}%</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabla */}
            <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm overflow-hidden">
                {isLoading && (
                    <div className="flex items-center justify-center p-10 text-gray-500">
                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                        Cargando estadísticas...
                    </div>
                )}
                {isError && (
                    <div className="flex flex-col items-center justify-center p-10 gap-4">
                        <p className="text-red-500">Error al cargar las estadísticas</p>
                        <button onClick={() => refetch()} className="px-4 py-2 bg-[#476c97] text-white rounded-md">
                            Reintentar
                        </button>
                    </div>
                )}
                {!isLoading && !isError && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    <th className="px-4 py-3">Partido</th>
                                    <th className="px-4 py-3">Resp. Municipio</th>
                                    <th className="px-4 py-3">Resp. Entidad</th>
                                    <th className="px-4 py-3">ID Red</th>
                                    <th className="px-4 py-3">Club</th>
                                    {BLOQUES.map(b => (
                                        <th key={b.clave} className="px-4 py-3 text-center">{b.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filas.length === 0 ? (
                                    <tr>
                                        <td colSpan={5 + BLOQUES.length} className="px-4 py-8 text-center text-gray-400">
                                            No se encontraron registros que coincidan con los filtros seleccionados.
                                        </td>
                                    </tr>
                                ) : filas.map((r, i) => (
                                    <tr key={r.id}
                                        onDoubleClick={() => setFilaDetalle(r)}
                                        className={`cursor-pointer border-b border-gray-100 dark:border-gray-700/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 ${i % 2 ? 'bg-gray-50/50 dark:bg-gray-900/20' : ''}`}>
                                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.partido_nombre}</td>
                                        <td className="px-4 py-2.5 text-center"><BadgeResponsable responsable={r.responsable_municipio} /></td>
                                        <td className="px-4 py-2.5 text-center"><BadgeResponsable responsable={r.responsable_entidad} /></td>
                                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-500 dark:text-gray-400">{r.id_red}</td>
                                        <td className="px-4 py-2.5 text-gray-900 dark:text-white font-medium">{r.nombre_completo}</td>
                                        {BLOQUES.map(b => (
                                            <td key={b.clave} className="px-4 py-2.5 text-center">
                                                <BadgePorcentaje cargados={r.bloque?.[b.clave]?.cargados || 0} total={r.bloque?.[b.clave]?.total || 0} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de detalle por pestaña */}
            <Modal
                isOpen={!!filaDetalle}
                onClose={() => setFilaDetalle(null)}
                title={filaDetalle ? `${filaDetalle.nombre_completo} (${filaDetalle.id_red})` : ''}
                maxWidth="max-w-3xl"
            >
                <div className="space-y-5">
                    {BLOQUES.map(b => {
                        const bl = filaDetalle?.bloque?.[b.clave] || { cargados: 0, total: 0 };
                        const pct = bl.total > 0 ? Math.round((bl.cargados / bl.total) * 100) : 0;
                        const barraColor = pct === 100 ? 'bg-green-500'
                            : pct >= 50 ? 'bg-amber-500'
                            : pct > 0 ? 'bg-orange-500'
                            : 'bg-gray-300 dark:bg-gray-600';
                        return (
                            <div key={b.clave} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        --{b.label}--
                                    </h3>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {bl.cargados} / {bl.total} campos ({pct}%)
                                    </span>
                                </div>
                                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div className={`h-full rounded-full ${barraColor}`} style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Modal>
        </div>
    );
};